import React, { useState, useEffect } from 'react';
import { CLIENT_LABEL_MAPPING } from '../utils/clientLabels';
import './LinkSelectorModal.css';

const LinkSelectorModal = ({ onClose, onSelect }) => {
    const [activeTab, setActiveTab] = useState('cliente');
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);

    // Selection state
    const [selectedClient, setSelectedClient] = useState(null);
    const [clientOrders, setClientOrders] = useState([]);

    const [employeeSummary, setEmployeeSummary] = useState(null);

    useEffect(() => {
        if (searchTerm.length >= 2 || !searchTerm) {
            handleSearch();
        }
    }, [searchTerm, activeTab]);

    const handleSearch = async () => {
        setLoading(true);
        try {
            let endpoint = '';
            if (activeTab === 'cliente') endpoint = `/api/clientes?search=${searchTerm}`;
            else if (activeTab === 'funcionario') endpoint = `/api/funcionarios?search=${searchTerm}`;
            else if (activeTab === 'fornecedor') endpoint = `/api/fornecedores?search=${searchTerm}&fields=NomeEmpresa,NomeDoContato`;

            const res = await fetch(endpoint, { credentials: 'include' });
            if (res.ok) {
                const data = await res.json();
                setResults(data);
            }
        } catch (error) {
            console.error('Erro na busca:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectClient = async (client) => {
        setSelectedClient(client);
        try {
            const res = await fetch(`/api/clientes/${client.ID}/pedidos`, { credentials: 'include' });
            if (res.ok) {
                const data = await res.json();
                setClientOrders(data);
            }
        } catch (error) {
            console.error('Erro ao buscar pedidos:', error);
        }
    };

    const handleSelectEmployee = async (emp) => {
        setLoading(true);
        try {
            const res = await fetch(`/api/financas/funcionario/${emp.ID}`, { credentials: 'include' });
            if (res.ok) {
                const data = await res.json();
                setEmployeeSummary({ employee: emp, ...data });
            } else {
                setEmployeeSummary({ employee: emp, transactions: [], summary: { pagar: 0, receber: 0, pago: 0, recebido: 0 } });
            }
        } catch (error) {
            console.error('Erro ao buscar resumo do funcionário:', error);
            setEmployeeSummary({ employee: emp, transactions: [], summary: { pagar: 0, receber: 0, pago: 0, recebido: 0 } });
        } finally {
            setLoading(false);
        }
    };

    const confirmSelection = (type, data) => {
        onSelect(type, data);
        onClose();
    };

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val || 0);
    };

    return (
        <div className="modal-overlay">
            <div className="modal-classic link-selector-modal">
                <div className="modal-header">
                    <span>Selecionar Vínculo Financeiro</span>
                    <button onClick={onClose}>X</button>
                </div>

                <div className="link-tabs">
                    <button className={activeTab === 'cliente' ? 'active' : ''} onClick={() => { setActiveTab('cliente'); setSelectedClient(null); }}>Clientes / Orçamentos</button>
                    <button className={activeTab === 'funcionario' ? 'active' : ''} onClick={() => { setActiveTab('funcionario'); setEmployeeSummary(null); }}>Funcionários</button>
                    <button className={activeTab === 'fornecedor' ? 'active' : ''} onClick={() => setActiveTab('fornecedor')}>Fornecedores</button>
                </div>

                <div className="modal-body">
                    <div className="search-bar">
                        <input
                            type="text"
                            placeholder={`Pesquisar ${activeTab}...`}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="selector-content">
                        {activeTab === 'cliente' && !selectedClient && (
                            <div className="list-results">
                                {results.map(c => (
                                    <div key={c.ID} className="result-item" onClick={() => handleSelectClient(c)}>
                                        <strong>{c.NomeDaEmpresa || `${CLIENT_LABEL_MAPPING['SobrenomeContato']}: ${c.SobrenomeContato}, ${c.PrimeiroNomeContato}`}</strong>
                                        <small>{c.Cidade}, {c.EstadoProvincia}</small>
                                    </div>
                                ))}
                            </div>
                        )}

                        {activeTab === 'cliente' && selectedClient && (
                            <div className="order-selection">
                                <button className="btn-back" onClick={() => setSelectedClient(null)}>← Voltar para Clientes</button>
                                <h4>Orçamentos de {selectedClient.SobrenomeContato}</h4>
                                <div className="list-results">
                                    {clientOrders.length === 0 ? <p>Nenhum orçamento encontrado.</p> :
                                        clientOrders.map(o => (
                                            <div key={o.ID} className="result-item order-item" onClick={() => confirmSelection('pedido', { ...o, clientName: selectedClient.SobrenomeContato })}>
                                                <span>Orçamento #{o.ID}</span>
                                                <span>Data: {new Date(o.DataPedido).toLocaleDateString('pt-BR')}</span>
                                                <strong>{formatCurrency(o.Frete)}</strong>
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                        )}

                        {activeTab === 'funcionario' && (
                            <div className="employee-layout">
                                <div className="list-results half">
                                    {results.map(e => (
                                        <div key={e.ID} className={`result-item ${employeeSummary?.employee?.ID === e.ID ? 'selected' : ''}`} onClick={() => handleSelectEmployee(e)}>
                                            <strong>{e.PrimeiroNome} {e.Sobrenome}</strong>
                                            <small>{e.Titulo}</small>
                                        </div>
                                    ))}
                                </div>
                                <div className="employee-summary-pane half">
                                    {employeeSummary ? (
                                        <div className="summary-details">
                                            <h4>Resumo: {employeeSummary.employee.PrimeiroNome}</h4>
                                            <div className="summary-boxes">
                                                <div className="s-box red">A Pagar: {formatCurrency(employeeSummary.summary.pagar)}</div>
                                                <div className="s-box green">Recebido: {formatCurrency(employeeSummary.summary.recebido)}</div>
                                            </div>
                                            <p><small>* Dados baseados em lançamentos vinculados.</small></p>
                                            <button className="btn-classic btn-full" onClick={() => confirmSelection('funcionario', employeeSummary.employee)}>Confirmar Seleção</button>
                                        </div>
                                    ) : (
                                        <div className="empty-hint">Selecione um funcionário para ver o resumo.</div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'fornecedor' && (
                            <div className="list-results">
                                {results.map(f => (
                                    <div key={f.ID} className="result-item" onClick={() => confirmSelection('fornecedor', f)}>
                                        <strong>{f.NomeEmpresa}</strong>
                                        <small>{f.NomeContato} - {f.Telefone}</small>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LinkSelectorModal;
